// Email Settings Test Page
// Test page for email settings functionality

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Mail, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Settings,
  TestTube
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import EmailSettings from '@/components/settings/EmailSettings';
import { checkN8NWebhookHealth } from '@/services/n8n/webhookService';
import AppLoadingSpinner from '@/components/loading/AppLoadingSpinner';

/**
 * Email Settings Test Page
 * Provides testing interface for email settings functionality
 */
const EmailSettingsTest = () => {
  const [testUser, setTestUser] = useState(null);
  const [webhookHealth, setWebhookHealth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Create a mock user for testing
  useEffect(() => {
    const mockUser = {
      id: 'test-user-123',
      email: 'test@example.com',
      name: 'Test User',
      created_at: new Date().toISOString()
    };
    setTestUser(mockUser);
    setIsLoading(false);
  }, []);

  // Test webhook health
  const testWebhookHealth = async () => {
    try {
      setIsLoading(true);
      const health = await checkN8NWebhookHealth();
      setWebhookHealth(health);
    } catch (error) {
      console.error('Webhook health test failed:', error);
      setWebhookHealth({
        status: 'error',
        message: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !testUser) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <AppLoadingSpinner message="Loading test page..." />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Test Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <TestTube className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Email Settings Test</h1>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          This page tests the email settings functionality including connection management, 
          sync controls, and webhook integration.
        </p>
      </div>

      {/* Test Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-5 w-5 text-blue-600" />
              <span className="font-medium">Test User</span>
            </div>
            <div className="text-sm text-gray-600">
              {testUser ? testUser.email : 'Not loaded'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-5 w-5 text-green-600" />
              <span className="font-medium">Components</span>
            </div>
            <div className="text-sm text-gray-600">
              EmailSettings, EmailConnectionStatus, SyncControls
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {webhookHealth ? (
                webhookHealth.status === 'healthy' ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )
              ) : (
                <Loader2 className="h-5 w-5 text-gray-600" />
              )}
              <span className="font-medium">Webhook Health</span>
            </div>
            <div className="text-sm text-gray-600">
              {webhookHealth ? webhookHealth.status : 'Not tested'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Webhook Health Test */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-600" />
            Webhook Health Test
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button 
              onClick={testWebhookHealth}
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Settings className="h-4 w-4 mr-2" />
              )}
              Test Webhook Health
            </Button>

            {webhookHealth && (
              <div className={`p-4 rounded-lg border ${
                webhookHealth.status === 'healthy' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {webhookHealth.status === 'healthy' ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className="font-medium">
                    {webhookHealth.status === 'healthy' ? 'Healthy' : 'Error'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {webhookHealth.message}
                </p>
                {webhookHealth.configured !== undefined && (
                  <Badge variant={webhookHealth.configured ? 'default' : 'destructive'} className="mt-2">
                    {webhookHealth.configured ? 'Configured' : 'Not Configured'}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Email Settings Component Test */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Email Settings Component
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The EmailSettings component below should display connection status, 
              sync controls, and management options. Test the following features:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Connection Management:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Connect Gmail account</li>
                  <li>• View connection status</li>
                  <li>• Reconnect expired tokens</li>
                  <li>• Disconnect account</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Sync Controls:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Toggle daily sync</li>
                  <li>• Manual sync now</li>
                  <li>• Re-scan historical emails</li>
                  <li>• View sync statistics</li>
                </ul>
              </div>
            </div>

            {/* Email Settings Component */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <EmailSettings user={testUser} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="h-5 w-5 text-brand-600" />
            Test Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Without Gmail Configuration:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Should show "Connect Gmail Account" prompt</li>
                  <li>• No error messages should appear</li>
                  <li>• OAuth component should be hidden</li>
                  <li>• Webhook health should show "not configured"</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">With Gmail Configuration:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Should show connection status</li>
                  <li>• Sync controls should be available</li>
                  <li>• Manual actions should work</li>
                  <li>• Statistics should display</li>
                </ul>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> To fully test Gmail integration, you'll need to configure 
                the Google OAuth credentials in your .env.local file. See SETUP_INSTRUCTIONS.md 
                for detailed setup instructions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettingsTest;
